// Kanban column "Awaiting human decision" (decision D23). The council decides whenever it can, so this column is
// rendered only while a task waits for a person. Each card is compact — number, "waiting for" badge, title, and a
// single "needs your decision" badge — the same as any other Kanban card (a Trello-style whole-card click opens
// TaskDetailDialog, which renders the actual question/options/glossary in its "Your decision" section).
import type { TaskCard } from "@maestros/contracts";
import { useI18n } from "../../i18n/index.tsx";
import { listProjectApprovals } from "../../api/endpoints-wave5.ts";
import { useAsync } from "../../lib/useAsync.ts";
import { elapsedSince, formatCompactDuration } from "../../lib/duration.ts";
import { useTickingNow } from "../../lib/ticker.ts";
import { TASK_STATUS_VIEW } from "../../lib/status.ts";
import { ErrorBanner } from "../../components/Feedback.tsx";
import { StatusIcon } from "../../components/Status.tsx";
import { ClockIcon } from "../../components/icons.tsx";

export function AwaitingHumanColumn({ projectId, tasks, onOpen }: { projectId: string; tasks: TaskCard[]; onOpen: (task: TaskCard) => void }) {
  const { t } = useI18n();
  // Refetched whenever the waiting tasks change (a decision moves one out, an escalation moves one in): the exact
  // requested_at gives a steadier "waiting for" badge than the task's own status_since.
  const key = tasks.map((x) => `${x.id}:${x.version}`).join(",");
  const approvals = useAsync((signal) => listProjectApprovals(projectId, "pending", signal), [projectId, key]);
  const requestedAt = new Map((approvals.data?.items ?? []).map((a) => [a.task_id, a.requested_at]));
  const view = TASK_STATUS_VIEW.awaiting_human;
  return (
    <li className="kanban-column kanban-column--human" data-column="awaiting_human" aria-labelledby="col-awaiting_human" data-testid="awaiting-human-column">
      <h3 id="col-awaiting_human" className="kanban-column-title">
        <StatusIcon kind={view.icon} />
        {t(view.label)}
        <span className="count">{tasks.length}</span>
      </h3>
      <p className="field-hint">{t("approval.columnHint")}</p>
      {approvals.error && !approvals.data ? <ErrorBanner error={approvals.error} onRetry={approvals.reload} title={t("approval.loadFailed")} /> : null}
      <ul className="kanban-cards">
        {tasks.map((task) => (
          <ApprovalCard key={task.id} task={task} requestedAt={requestedAt.get(task.id) ?? null} onOpen={onOpen} />
        ))}
      </ul>
    </li>
  );
}

function ApprovalCard({ task, requestedAt, onOpen }: { task: TaskCard; requestedAt: string | null; onOpen: (task: TaskCard) => void }) {
  const { t } = useI18n();
  const now = useTickingNow();
  const waited = formatCompactDuration(elapsedSince(requestedAt ?? task.status_since, now) ?? Number.NaN);
  return (
    <li className="task-card approval-card" data-testid={`approval-card-${task.number}`} data-task-id={task.id}>
      <div className="task-card-head">
        <span className="mono subtle">#{task.number}</span>
        {waited ? (
          <span className="badge badge--warning" data-testid="approval-waiting" aria-live="off">
            <ClockIcon size={12} aria-hidden="true" />
            {t("approval.waitingFor", { duration: waited })}
          </span>
        ) : null}
      </div>
      <button type="button" className="task-card-title" onClick={() => onOpen(task)}>
        <span className="user-content">{task.title}</span>
      </button>
      <span className="badge badge--warning" data-testid="approval-needs-decision">
        {t("approval.needsDecision")}
      </span>
    </li>
  );
}
